import { db } from './firebase';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';

class ProfileService {
  constructor() {
    this.collectionName = 'users';
  }

  /**
   * Kullanıcı profilini Firebase'den getir
   */
  async getUserProfile(userId) {
    try {
      console.log('📥 Getting user profile from Firebase:', userId);
      
      const userRef = doc(db, this.collectionName, userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = { id: userSnap.id, ...userSnap.data() };
        console.log('✅ User profile retrieved:', userData);
        return userData;
      } else {
        console.warn('⚠️ User profile not found in Firebase');
        return null;
      }
    } catch (error) {
      console.error('❌ Error getting user profile:', error);
      throw error;
    }
  }

  /**
   * Kullanıcı profilini Firebase'de güncelle
   */
  async updateUserProfile(userId, profileData) {
    try {
      console.log('📤 Updating user profile in Firebase:', userId, profileData);
      
      // Telefon numarasını format kontrolü
      const formattedProfileData = {
        ...profileData,
        phone: this.formatPhoneNumber(profileData.phone),
        updated_at: new Date(),
        last_profile_update: new Date()
      };

      const userRef = doc(db, this.collectionName, userId);
      
      // Mevcut kullanıcı var mı kontrol et
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        // Güncelle
        await updateDoc(userRef, formattedProfileData);
        console.log('✅ User profile updated successfully');
      } else {
        // Yeni kullanıcı oluştur
        await setDoc(userRef, {
          id: userId,
          ...formattedProfileData,
          created_at: new Date()
        });
        console.log('✅ New user profile created successfully');
      }

      // Güncellenmiş veriyi getir ve döndür
      const updatedUserSnap = await getDoc(userRef);
      const updatedUserData = { id: updatedUserSnap.id, ...updatedUserSnap.data() };
      
      return updatedUserData;
    } catch (error) {
      console.error('❌ Error updating user profile:', error);
      throw error;
    }
  }

  /**
   * Şifre değiştir
   */
  async changePassword(userId, currentPassword, newPassword) {
    try {
      console.log('🔒 Changing password for user:', userId);
      
      // Firebase Authentication için ayrı bir servis gerekebilir
      // Şu an sadece Firestore'da güncelleme yapıyoruz
      const userRef = doc(db, this.collectionName, userId);
      
      await updateDoc(userRef, {
        password_changed_at: new Date(),
        // Şifre hash'ini burada saklamayız, bu Authentication katmanında olmalı
      });

      console.log('✅ Password change logged successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ Error changing password:', error);
      throw error;
    }
  }

  /**
   * E-posta doğrulama gönder
   */
  async sendEmailVerification(userId, email) {
    try {
      console.log('📧 Sending email verification:', email);
      
      const userRef = doc(db, this.collectionName, userId);
      
      await updateDoc(userRef, {
        email_verification_sent_at: new Date(),
        email_verification_status: 'pending'
      });

      // Burada gerçek e-posta gönderme servisi çağrılacak
      // Örnek: sendVerificationEmail(email)
      
      console.log('✅ Email verification request logged');
      return { success: true };
    } catch (error) {
      console.error('❌ Error sending email verification:', error);
      throw error;
    }
  }

  /**
   * Telefon doğrulama gönder
   */
  async sendPhoneVerification(userId, phone) {
    try {
      console.log('📱 Sending phone verification:', phone);
      
      const userRef = doc(db, this.collectionName, userId);
      
      await updateDoc(userRef, {
        phone_verification_sent_at: new Date(),
        phone_verification_status: 'pending'
      });

      // Burada gerçek SMS gönderme servisi çağrılacak
      // Örnek: sendVerificationSMS(phone)
      
      console.log('✅ Phone verification request logged');
      return { success: true };
    } catch (error) {
      console.error('❌ Error sending phone verification:', error);
      throw error;
    }
  }

  /**
   * Kullanıcı kayıt işlemi
   */
  async registerUser(userData) {
    try {
      console.log('👤 Registering new user:', userData.email);
      
      // Kullanıcı ID'si e-posta bazlı veya Firebase Auth'dan gelecek
      const userId = userData.email.replace(/[@.]/g, '_'); // Geçici ID
      
      const formattedUserData = {
        id: userId,
        first_name: userData.first_name,
        last_name: userData.last_name,
        name: `${userData.first_name} ${userData.last_name}`,
        full_name: `${userData.first_name} ${userData.last_name}`,
        email: userData.email,
        username: userData.username || userData.email,
        phone: this.formatPhoneNumber(userData.phone),
        company_name: userData.company_name || '',
        user_type: userData.user_type || 'business_owner',
        role: userData.role || 'business_owner',
        is_active: true,
        email_verified: false,
        phone_verified: false,
        created_at: new Date(),
        updated_at: new Date(),
        last_login: null,
        profile_completed: true
      };

      const userRef = doc(db, this.collectionName, userId);
      await setDoc(userRef, formattedUserData);
      
      console.log('✅ User registered successfully');
      return formattedUserData;
    } catch (error) {
      console.error('❌ Error registering user:', error);
      throw error;
    }
  }

  /**
   * Kullanıcı giriş işlemi sonrası profil güncelleme
   */
  async updateLoginInfo(userId, loginData) {
    try {
      console.log('🔐 Updating login info for user:', userId);
      
      const userRef = doc(db, this.collectionName, userId);
      
      await updateDoc(userRef, {
        last_login: new Date(),
        login_count: (loginData.login_count || 0) + 1,
        last_ip: loginData.ip_address || null,
        last_user_agent: loginData.user_agent || null
      });

      console.log('✅ Login info updated successfully');
    } catch (error) {
      console.error('❌ Error updating login info:', error);
      // Login info update hatasında kullanıcı deneyimini bozmayalım
    }
  }

  /**
   * Telefon numarasını formatla
   */
  formatPhoneNumber(phone) {
    if (!phone) return '';
    
    // Sadece rakamları al
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Türkiye numarası kontrolü
    if (digitsOnly.startsWith('90')) {
      return '+' + digitsOnly;
    } else if (digitsOnly.startsWith('0')) {
      return '+90' + digitsOnly.substring(1);
    } else if (digitsOnly.length === 10) {
      return '+90' + digitsOnly;
    }
    
    return phone; // Diğer ülke kodları için olduğu gibi döndür
  }

  /**
   * LocalStorage ile senkronizasyon
   */
  syncWithLocalStorage(userData) {
    try {
      // Mevcut localStorage verisiyle merge et
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      const mergedUser = {
        ...currentUser,
        ...userData,
        // Firebase'den gelen timestamp'lari string'e çevir
        created_at: userData.created_at?.toISOString?.() || userData.created_at,
        updated_at: userData.updated_at?.toISOString?.() || userData.updated_at,
        last_login: userData.last_login?.toISOString?.() || userData.last_login
      };
      
      localStorage.setItem('user', JSON.stringify(mergedUser));
      
      // Header'ı güncelle
      window.dispatchEvent(new CustomEvent('userProfileUpdated', { 
        detail: mergedUser 
      }));
      
      console.log('✅ Profile synced with localStorage');
      return mergedUser;
    } catch (error) {
      console.error('❌ Error syncing with localStorage:', error);
      throw error;
    }
  }

  /**
   * Kullanıcı profilini tamamen senkronize et (Firebase + localStorage)
   */
  async syncUserProfile(userId) {
    try {
      console.log('🔄 Syncing user profile:', userId);
      
      // Firebase'den güncel veriyi çek
      const firebaseUser = await this.getUserProfile(userId);
      
      if (firebaseUser) {
        // LocalStorage ile senkronize et
        const syncedUser = this.syncWithLocalStorage(firebaseUser);
        return syncedUser;
      }
      
      return null;
    } catch (error) {
      console.error('❌ Error syncing user profile:', error);
      throw error;
    }
  }
}

export default new ProfileService();